import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function SEOHead() {
  const location = useLocation();

  useEffect(() => {
    // Remove any existing meta tags and links that we'll be managing
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    const existingRobots = document.querySelector('meta[name="robots"]');
    const existingNoindex = document.querySelector('meta[name="robots"][content*="noindex"]');
    
    if (existingCanonical) existingCanonical.remove();
    if (existingRobots) existingRobots.remove();
    if (existingNoindex) existingNoindex.remove();

    // Create canonical link
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    
    const baseUrl = 'https://nechcilepek.cz';
    const currentPath = location.pathname;
    
    // Handle different page types
    if (currentPath === '/') {
      canonicalLink.href = baseUrl;
    } else if (currentPath === '/404' || currentPath === '*') {
      // For 404 pages, use a specific canonical and add noindex
      canonicalLink.href = `${baseUrl}/404`;
      
      const noindexMeta = document.createElement('meta');
      noindexMeta.name = 'robots';
      noindexMeta.content = 'noindex, nofollow, noarchive, nosnippet';
      document.head.appendChild(noindexMeta);
    } else {
      // For other pages, use the full path
      canonicalLink.href = `${baseUrl}${currentPath}`;
    }
    
    document.head.appendChild(canonicalLink);

    // Add page-specific meta tags and structured data
    const pageMeta = getPageMeta(currentPath);
    if (pageMeta) {
      // Update title if different from default
      if (pageMeta.title) {
        document.title = pageMeta.title;
      }
      
      // Update description if different from default
      if (pageMeta.description) {
        let descMeta = document.querySelector('meta[name="description"]');
        if (!descMeta) {
          descMeta = document.createElement('meta');
          descMeta.name = 'description';
          document.head.appendChild(descMeta);
        }
        descMeta.content = pageMeta.description;
      }

      // Add schema.org structured data
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        'name': 'Nechci Lepek',
        'applicationCategory': 'HealthApplication',
        'operatingSystem': 'Web',
        'description': pageMeta.description,
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'CZK'
        },
        'author': {
          '@type': 'Organization',
          'name': 'Nechci Lepek',
          'url': 'https://nechcilepek.cz'
        }
      };

      // Add article schema for content pages
      if (currentPath === '/') {
        structuredData.mainEntity = {
          '@type': 'Article',
          'headline': 'Průvodce bezlepkovou dietou',
          'description': 'Komplexní průvodce bezlepkovou dietou, včetně informací o celiaklii, bezlepkových potravinách a jejich identifikaci.',
          'articleBody': 'Bezlepková dieta je způsob stravování, při kterém se vylučují všechny potraviny obsahující lepek...',
          'datePublished': '2024-03-15',
          'dateModified': new Date().toISOString().split('T')[0]
        };
      }

      // Add FAQ schema for the FAQ section
      if (currentPath === '/') {
        structuredData.hasPart = {
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'Je aplikace zdarma?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Ano, základní funkce aplikace Nechci Lepek jsou zcela zdarma. Můžete skenovat neomezené množství produktů a získávat informace o obsahu lepku bez jakýchkoli poplatků.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Jak přesné jsou informace o produktech?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Informace čerpáme z databáze, která obsahuje ověřené údaje od výrobců. Doporučujeme však vždy zkontrolovat seznam složek na obalu produktu.'
              }
            }
          ]
        };
      }

      // Add structured data to head
      const scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.text = JSON.stringify(structuredData);
      document.head.appendChild(scriptTag);
    }

    // Cleanup function
    return () => {
      const canonicalToRemove = document.querySelector('link[rel="canonical"]');
      const robotsToRemove = document.querySelector('meta[name="robots"]');
      if (canonicalToRemove) canonicalToRemove.remove();
      if (robotsToRemove) robotsToRemove.remove();
    };
  }, [location]);

  // Helper function to get page-specific meta information
  const getPageMeta = (path) => {
    const metaData = {
      '/': {
        title: 'Nechci Lepek - Bezlepkový skener potravin',
        description: 'Skenujte čárové kódy a okamžitě zjistěte, zda je produkt bezlepkový. Pro osoby s celiaklií.'
      },
      '/about': {
        title: 'O aplikaci - Nechci Lepek',
        description: 'Informace o aplikaci Nechci Lepek pro skenování bezlepkových potravin a pomoc osobám s celiaklií.'
      },
      '/contact': {
        title: 'Kontakt - Nechci Lepek',
        description: 'Kontaktujte tým Nechci Lepek pro dotazy, připomínky nebo spolupráci.'
      },
      '/terms': {
        title: 'Podmínky použití - Nechci Lepek',
        description: 'Podmínky použití aplikace Nechci Lepek pro skenování bezlepkových potravin.'
      },
      '/privacy': {
        title: 'Ochrana údajů - Nechci Lepek',
        description: 'Zásady ochrany osobních údajů a soukromí v aplikaci Nechci Lepek.'
      },
      '/404': {
        title: 'Stránka nenalezena - Nechci Lepek',
        description: 'Omlouváme se, ale stránka, kterou hledáte, neexistuje nebo byla přesunuta.'
      }
    };
    
    return metaData[path] || metaData['/'];
  };

  return null; // This component doesn't render anything
}

export default SEOHead; 